/**
 * Centralized API utility for consistent XSRF token handling
 *
 * This utility ensures all internal API calls use proper CSRF protection
 * by including XSRF tokens from cookies and setting appropriate headers.
 */

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  credentials?: RequestCredentials;
}

/**
 * Get XSRF token from cookies
 * Laravel automatically sets this cookie for authenticated sessions
 */
export const getXsrfToken = (): string | null => {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  if (match && match[1]) {
    // The cookie value is URL-encoded, so we need to decode it
    return decodeURIComponent(match[1]);
  }
  return null;
};

/**
 * Make an authenticated API request with proper XSRF token handling
 *
 * @param url - The API endpoint URL
 * @param options - Request options (method, body, etc.)
 * @returns Promise<Response>
 *
 * @example
 * // GET request
 * const response = await apiRequest('/api/agents');
 *
 * @example
 * // POST request
 * const response = await apiRequest('/api/agents', {
 *   method: 'POST',
 *   body: { name: 'New Agent', description: 'A test agent' }
 * });
 *
 * @example
 * // PUT request
 * const response = await apiRequest('/api/agents/1', {
 *   method: 'PUT',
 *   body: { name: 'Updated Agent' }
 * });
 */
export const apiRequest = async (url: string, options: ApiRequestOptions = {}): Promise<Response> => {
  const { method = 'GET', headers = {}, body, credentials = 'include', ...restOptions } = options;

  // Get XSRF token
  const xsrfToken = getXsrfToken();

  // Prepare headers with XSRF token and standard headers
  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...headers,
  };

  // Add XSRF token for all requests (GET, POST, PUT, DELETE)
  if (xsrfToken) {
    requestHeaders['X-XSRF-TOKEN'] = xsrfToken;
  }

  // Add Content-Type header for requests with body
  if (body && !requestHeaders['Content-Type']) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  // Prepare request options
  const requestOptions: RequestInit = {
    method,
    headers: requestHeaders,
    credentials,
    ...restOptions,
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    requestOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  return fetch(url, requestOptions);
};

/**
 * Convenience method for GET requests
 *
 * @param url - The API endpoint URL
 * @param options - Additional request options
 * @returns Promise<Response>
 */
export const apiGet = (url: string, options: Omit<ApiRequestOptions, 'method'> = {}): Promise<Response> => {
  return apiRequest(url, { ...options, method: 'GET' });
};

/**
 * Convenience method for POST requests
 *
 * @param url - The API endpoint URL
 * @param body - Request body data
 * @param options - Additional request options
 * @returns Promise<Response>
 */
export const apiPost = (url: string, body?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<Response> => {
  return apiRequest(url, { ...options, method: 'POST', body });
};

/**
 * Convenience method for PUT requests
 *
 * @param url - The API endpoint URL
 * @param body - Request body data
 * @param options - Additional request options
 * @returns Promise<Response>
 */
export const apiPut = (url: string, body?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<Response> => {
  return apiRequest(url, { ...options, method: 'PUT', body });
};

/**
 * Convenience method for DELETE requests
 *
 * @param url - The API endpoint URL
 * @param options - Additional request options
 * @returns Promise<Response>
 */
export const apiDelete = (url: string, options: Omit<ApiRequestOptions, 'method'> = {}): Promise<Response> => {
  return apiRequest(url, { ...options, method: 'DELETE' });
};

/**
 * Convenience method for PATCH requests
 *
 * @param url - The API endpoint URL
 * @param body - Request body data
 * @param options - Additional request options
 * @returns Promise<Response>
 */
export const apiPatch = (url: string, body?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<Response> => {
  return apiRequest(url, { ...options, method: 'PATCH', body });
};

/**
 * Helper to parse JSON response and handle errors
 */
const parseJsonResponse = async (response: Response): Promise<any> => {
  try {
    const data = await response.json();

    // If the response has an error status code but the JSON parsed successfully,
    // it might contain error information from Laravel
    if (!response.ok && data.success === false) {
      return data; // Return the error response so the caller can handle it
    }

    return data;
  } catch {
    // If JSON parsing fails, create a structured error response
    return {
      success: false,
      error: `HTTP ${response.status}: ${response.statusText}`,
      message: 'Failed to parse response JSON',
    };
  }
};

/**
 * JSON-parsing convenience methods that return parsed data instead of raw Response
 */

export const apiGetJson = async (url: string, options: Omit<ApiRequestOptions, 'method'> = {}): Promise<any> => {
  const response = await apiGet(url, options);
  return parseJsonResponse(response);
};

export const apiPostJson = async (url: string, body?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<any> => {
  const response = await apiPost(url, body, options);
  return parseJsonResponse(response);
};

export const apiPutJson = async (url: string, body?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<any> => {
  const response = await apiPut(url, body, options);
  return parseJsonResponse(response);
};

export const apiDeleteJson = async (url: string, options: Omit<ApiRequestOptions, 'method'> = {}): Promise<any> => {
  const response = await apiDelete(url, options);
  return parseJsonResponse(response);
};

export const apiPatchJson = async (url: string, body?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<any> => {
  const response = await apiPatch(url, body, options);
  return parseJsonResponse(response);
};

/**
 * Convenience method for POST requests with FormData (multipart/form-data)
 * Used for file uploads where Content-Type must be automatically set by browser
 *
 * @param url - The API endpoint URL
 * @param formData - FormData object containing files and other data
 * @param options - Additional request options
 * @returns Promise<Response>
 *
 * @example
 * const formData = new FormData();
 * formData.append('message', 'Hello');
 * formData.append('attachments[]', file1);
 * formData.append('attachments[]', file2);
 * const response = await apiPostFormData('/api/chat-messages', formData);
 */
export const apiPostFormData = async (
  url: string,
  formData: FormData,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {},
): Promise<Response> => {
  const { headers = {}, credentials = 'include', ...restOptions } = options;

  // Get XSRF token
  const xsrfToken = getXsrfToken();

  // Prepare headers - DO NOT set Content-Type, browser will set it with boundary
  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...headers,
  };

  // Add XSRF token
  if (xsrfToken) {
    requestHeaders['X-XSRF-TOKEN'] = xsrfToken;
  }

  return fetch(url, {
    method: 'POST',
    headers: requestHeaders,
    credentials,
    body: formData,
    ...restOptions,
  });
};

/**
 * JSON-parsing convenience method for FormData POST requests
 */
export const apiPostFormDataJson = async (
  url: string,
  formData: FormData,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {},
): Promise<any> => {
  const response = await apiPostFormData(url, formData, options);
  return parseJsonResponse(response);
};
